import type { LinkProps } from 'next/link';
import { type MouseEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ProductTypeEnum } from '@/types/product';
import { hasAddedLines } from '@/utils/checkout';
import { getLabel } from '@/utils/labelUtils';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { ProductCard as ProductCardCompound } from '@/components/ProductCard';
import { getProductCardImageAlt } from '@/components/ProductCard/helpers';
import { Visibility } from '@/components/Visibility';
import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import type { ContentfulProductCardFragment } from '@/graphql/contentful/api';
import {
  type OrderAppCtaType,
  type OrderAppProductVariantsBusinessDetailsQuery,
  useProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import type {
  ProductMediaFragment,
  TaxedMoneyFragment,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import type { ProductCardFragmentWithVariantCta } from '@/hooks/useFetchProductsWithCta';
import { useProcessingLine } from '@/hooks/useProcessingLine';
import { useRouter } from '@/hooks/useRouter';
import {
  useSendAddedToCartAlgoliaEvent,
  useSendClickedAlgoliaEvent,
} from '@/hooks/useSendAlgoliaEvent';
import { useStrapPurchaseFlow } from '@/hooks/useStrapPurchaseFlow';
import type { StrapHit } from '@/integrations/algolia/types';
import { findProductCtaFromBusinessData } from '@/integrations/orderApp';
import {
  getBoolValue,
  getDropdownValue,
  getMultiselectValue,
} from '@/lib/attributes';
import { allowedCountries } from '@/lib/eccomerceCountries';
import { getTranslatedName } from '@/lib/i18n';
import {
  findMediaByShotType,
  getImageScaleRatioByProduct,
  getProductPath,
  getProductType,
  getTranslatedProductName,
} from '@/lib/product';
import { generateDataTestId } from '@/lib/tests';
import type { CtaType, Maybe, WithHTMLProp } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { AddToBagDrawer } from '@/views/StrapView/components/AddToBagDrawer';

import * as S from './ProductCard.styles';

export enum ProductCardTypeEnum {
  DEFAULT = 'Default',
  WATCH_WRIST_ON_HOVER = 'Watch-WristOnHover',
  WATCH_MULTI_IMAGE = 'Watch-MultiImage',
}
export interface ProductCardProps extends Partial<ContentfulProductCardFragment> {
  autoLoadCTA?: boolean;
  href?: LinkProps['href'];
  isLinkToCollection?: boolean;
  preload?: boolean;
  productData?: ProductCardFragmentWithVariantCta;
  showCollection?: boolean;
  showName?: boolean;
  showSku?: boolean;
  sizes?: string;
}

export const ProductCard = ({
  autoLoadCTA = false,
  backgroundColor,
  href,
  isLinkToCollection,
  productData: product,
  showCollection = false,
  showCta,
  showLabel,
  showName = true,
  showPrice,
  showShare,
  showSku = true,
  showWishlist,
  sizes,
  type = 'Default',
  variant = 'Default',
}: ProductCardProps) => {
  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const {
    lineAdd,
    snapshot: { isProcessing: isCheckoutProcessing },
  } = useCheckout();
  const { isBeingProcessed, setProcessing } = useProcessingLine();
  const [isHovered, setIsHovered] = useState(false);
  const { country, locale, productCatalogMode, query } = useRegionProvider();
  const router = useRouter();
  const { formatMessage } = useIntl();
  const sendClickedAlgoliaEvent = useSendClickedAlgoliaEvent();
  const sendAddedToCartAlgoliaEvent = useSendAddedToCartAlgoliaEvent();

  const [orderAppData, setOrderAppData] = useState<
    OrderAppProductVariantsBusinessDetailsQuery | undefined
  >(undefined);

  const { refetch: fetchCTA } = useProductVariantsBusinessDetailsQuery({
    variables: { skus: [], channel: query.channel },
    notifyOnNetworkStatusChange: true,
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.orderApp,
    }),
    skip: true,
  });

  const sku = product?.defaultVariant?.sku;

  const {
    buckles,
    handlePurchase,
    isDrawerOpen: isAddToBagModalOpen,
    setIsDrawerOpen: setIsAddToBagModalOpen,
    sizeState,
    strapData,
    tools,
  } = useStrapPurchaseFlow(sku as string, product?.defaultVariant?.id);

  const productId = product?.defaultVariant?.id;
  const productType = getProductType(product?.productType);
  const isStrap = productType === 'strap';

  const ecommCountry = allowedCountries?.includes(country.code);

  const marketingStatus = getMultiselectValue(product?.marketingStatus, false);

  let launchDate: number | string;

  if (product?.productType.slug === ProductTypeEnum.Eyewear) {
    launchDate = product?.launch_date ?? 0;
  } else {
    launchDate = product?.launchDate?.values?.[0]?.name ?? 0;
  }

  const label = getLabel(marketingStatus, launchDate);

  useEffect(() => {
    if (sku && showCta && autoLoadCTA && !productCatalogMode && ecommCountry) {
      (async () => {
        const { data } = await fetchCTA({
          skus: [sku],
          channel: query.channel,
        });
        setOrderAppData(data);
      })();
    }
  }, [sku, showCta, autoLoadCTA, productCatalogMode]);

  if (!product) {
    return <ImagePlaceholder data-test-id='ProductCardImagePlaceholder' />;
  }

  const sendProductAddedToCart = (productSku: string) =>
    sendAddedToCartAlgoliaEvent(
      productSku,
      product.defaultVariant?.pricing?.price?.gross.amount,
      product.defaultVariant?.pricing?.price?.gross.currency,
    );

  const addToBag = async (
    id: string,
    sku: Maybe<string>,
    cta: 'ORDER' | 'PRE_ORDER',
  ) => {
    setProcessing(id);
    if (isStrap) {
      const shouldOpenDrawer = await handlePurchase();
      if (shouldOpenDrawer !== false) {
        setProcessing();
        return;
      }
    }
    const addedLines = await lineAdd(id, cta);
    if (sku && hasAddedLines(addedLines)) {
      sendProductAddedToCart(sku);
    }
    setProcessing();
  };

  const handleAddToCart = async (
    e: MouseEvent<HTMLElement>,
    id: Maybe<string>,
    sku: Maybe<string>,
    cta: CtaType | undefined,
    path: LinkProps['href'],
  ) => {
    e.preventDefault();
    e.stopPropagation();

    switch (cta) {
      case 'ORDER':
      case 'PRE_ORDER':
        if (id) {
          await addToBag(id, sku, cta);
        }
        break;
      case 'NOTIFY':
        if (sku && 'WATCH' === productType.toUpperCase()) {
          openNotifyMe({ sku });
        }
        break;
      case 'NO_CTA':
        setContactUsModalStatus(true, sku);
        break;
      case 'DISCOVER':
        router.push(path);
        break;
      default:
        break;
    }
  };

  const cta = productCatalogMode
    ? 'DISCOVER'
    : product.variantCTA || findProductCtaFromBusinessData(orderAppData, sku);

  const isStrapAndnoIntegratedBuckle =
    isStrap && !getBoolValue(product.integratedBuckle);

  const shareableProductData = {
    sku: product.defaultVariant?.sku,
    name: product.name,
    collection: product.collections?.[0]?.name,
    image: product.media?.[0]?.url,
    url: [
      process.env.NEXT_PUBLIC_URL,
      country.code.toLowerCase(),
      '-',
      locale.slug,
      getProductPath(product, true),
    ].join(''),
  };
  const name = getTranslatedProductName(product);
  const collectionName =
    getDropdownValue(product.collectionName) ||
    getTranslatedName('name', product.collections?.[0]);
  const strapHit: Partial<StrapHit> = {
    objectID: product.defaultVariant?.sku ?? '',
    name: {
      [locale.code.toUpperCase()]: name ?? '',
    },
    price: product.defaultVariant?.pricing?.price?.gross?.amount,
    currency: product.defaultVariant?.pricing?.price?.gross?.currency,
    integrated_buckle: getBoolValue(product.integratedBuckle),
    variant_id: product.defaultVariant?.id,
  };

  return (
    <>
      <S.Wrapper
        variant={variant ?? 'Default'}
        {...generateDataTestId('productCard', 'forCMS')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <S.ProductCardWrapper
          $variant={variant ?? 'Default'}
          backgroundColor={backgroundColor?.value}
          cardClickHandler={() =>
            sku && !isLinkToCollection && sendClickedAlgoliaEvent(sku)
          }
          cta={
            productId &&
            showCta && (
              <ProductCardCompound.CTA
                className={
                  type === ProductCardTypeEnum.WATCH_WRIST_ON_HOVER
                    ? 'trending-now'
                    : ''
                }
                cta={cta}
                data-test-id='ProductCardCTA'
                disabled={isCheckoutProcessing || !cta}
                isProcessing={isBeingProcessed(productId) || !cta}
                onClick={(e) =>
                  handleAddToCart(
                    e,
                    productId,
                    product.defaultVariant?.sku,
                    cta,
                    href ?? getProductPath(product),
                  )
                }
              />
            )
          }
          ctaType='bottom'
          href={href ?? getProductPath(product)}
          sku={product.slug}
          topSlot={
            sku && (
              <Visibility handleDisplay={false} xsHidden={!showWishlist}>
                <ProductCardCompound.Favorite
                  isFavIconWhite={
                    type === ProductCardTypeEnum.WATCH_WRIST_ON_HOVER
                  }
                  isHovered={isHovered}
                  name={name}
                  price={product.defaultVariant?.pricing?.price?.gross.amount}
                  sku={sku}
                  theCurrency={
                    product.defaultVariant?.pricing?.price?.gross.currency
                  }
                  type={product.productType?.name}
                />
              </Visibility>
            )
          }
          type={product.productType.name}
        >
          <ProductImage
            alt={getProductCardImageAlt({
              formatMessage,
              isLinkToCollection,
              name,
              sku,
              collectionName,
            })}
            className={`cms-product-card ${productType}-card`}
            data-test-id='ProductCardProductImage'
            isHovered={isHovered}
            product={product}
            sizes={sizes}
            type={type ?? 'Default'}
            variant={variant ?? 'Default'}
          />
          {showLabel && label && (!isHovered || type === 'Default') && (
            <ProductCardCompound.Label
              data-test-id='ProductCardLabel'
              text={<FormattedMessage {...label} />}
            />
          )}
          {showShare && (
            <ProductCardCompound.Share
              shareableProductData={shareableProductData}
            />
          )}
          <S.ProductCardDetails data-test-id='ProductCardDetails' gap={2}>
            {(showSku || !showCollection) && (
              <S.VisibilityWrapper
                data-test-id='ProductCardVisibilityWrapper-Sku'
                handleDisplay={false}
                xsHidden={!showSku}
              >
                <ProductCardCompound.Sku>
                  {product.defaultVariant?.sku}
                </ProductCardCompound.Sku>
              </S.VisibilityWrapper>
            )}
            {showName && (
              <S.ProductCardName data-test-id='ProductCardName'>
                {name}
              </S.ProductCardName>
            )}
            {showCollection && (
              <S.ProductCardName data-test-id='ProductCardCollectionName'>
                {getDropdownValue(product.collectionName) ||
                  getTranslatedName('name', product.collections?.[0])}
              </S.ProductCardName>
            )}
            {showPrice && (
              <ProductCardCompound.Price
                onClick={() => null}
                price={product.defaultVariant?.pricing?.price?.gross}
              />
            )}
            {isStrapAndnoIntegratedBuckle && (
              <ProductCardCompound.AdditionalInfo data-test-id='ProductCardAdditionalInfo'>
                <FormattedMessage {...commonMessages.buckleNotIncluded} />
              </ProductCardCompound.AdditionalInfo>
            )}
          </S.ProductCardDetails>
        </S.ProductCardWrapper>
      </S.Wrapper>
      {isStrap && strapData.businessData && (
        <AddToBagDrawer
          buckles={buckles}
          businessData={strapData.businessData}
          cta={cta as OrderAppCtaType}
          data-test-id='ProductCardAddToBagDrawer'
          isOpen={isAddToBagModalOpen}
          niceToHaveProducts={tools}
          onAddedToBag={() => sku && sendProductAddedToCart(sku)}
          setIsOpen={setIsAddToBagModalOpen}
          sizeState={sizeState}
          strap={strapHit}
          strapId={productId as string}
          strapPrice={
            product.defaultVariant?.pricing?.price as TaxedMoneyFragment
          }
          strapSku={product.defaultVariant?.sku}
        />
      )}
    </>
  );
};

interface ProductImageProps extends WithHTMLProp<'className'> {
  alt?: string;
  isHovered?: boolean;
  loading?: 'lazy' | 'eager';
  product: ProductCardFragmentWithVariantCta;
  sizes?: string;
  type: string;
  variant: string;
}
const ProductImage = ({
  alt,
  className,
  isHovered,
  loading = 'lazy',
  product,
  sizes,
  type,
  variant,
}: ProductImageProps) => {
  // Always prefer Soldier image for product cards
  const media: ProductMediaFragment | undefined = product.media
    ? (findMediaByShotType(product.media, 'Soldier') ?? product.media[0])
    : (product.thumbnail as ProductMediaFragment);
  const productType = getProductType(product.productType);
  const mediaTwo: ProductMediaFragment | undefined =
    findMediaByShotType(product.media ?? [], 'On wrist') ??
    findMediaByShotType(product.media ?? [], 'Three-quarter') ??
    undefined;

  if (type === ProductCardTypeEnum.WATCH_WRIST_ON_HOVER) {
    return (
      <S.ProductAlternateImgImage
        alt={alt}
        className={className}
        data-test-id='ProductImageAlternateImg'
        isHovered={isHovered}
        loading={loading}
        media={media}
        mediaTwo={mediaTwo}
        productType={productType}
        sizes={sizes ?? '25vw'}
        variant={variant ?? 'Default'}
        withBackground={false}
      />
    );
  } else if (type === ProductCardTypeEnum.WATCH_MULTI_IMAGE) {
    return (
      <S.ProductSlider
        alt={alt}
        data-test-id='ProductSlider'
        media={product.media as Array<ProductMediaFragment>}
        sizes={sizes ?? '50vw'}
        variant={variant ?? 'Default'}
      />
    );
  }

  return (
    <S.ProductImage
      alt={alt || media?.alt || ''}
      className={className}
      data-test-id='ProductImage'
      loading={loading}
      productType={productType}
      sizes={sizes ?? '25vw'}
      src={media?.url || ''}
      variant={variant ?? 'Default'}
      withBackground={false}
      zoom={getImageScaleRatioByProduct(product) ?? 1}
    />
  );
};
